import {
    FILTER_MODE_DEFINITIONS,
    FilterMode,
    FilterValueWithMode,
    isStringMode,
    oppositeMode,
} from "@impero-com/ui/domain";
import { Row } from "@tanstack/react-table";

function applyFilterMode(matches: boolean, mode: FilterMode): boolean {
    return FILTER_MODE_DEFINITIONS[mode].negated ? !matches : matches;
}

function substringMatches(cellValue: unknown, query: string): boolean {
    const values = Array.isArray(cellValue) ? cellValue : [cellValue];
    return values.some(
        (item) => typeof item === "string" && item.toLowerCase().includes(query)
    );
}

type MembershipFn<TRow, TFilterValue> = (
    row: Row<TRow>,
    value: TFilterValue
) => boolean;

function createModeFilterFn<TRow, TFilterValue>(
    membershipFn: MembershipFn<TRow, TFilterValue> | undefined
) {
    // One condition of the filter: the main value under `mode`, or the
    // exception under the opposite mode.
    const matchesCondition = (
        row: Row<TRow>,
        id: string,
        value: TFilterValue | null | undefined,
        mode: FilterMode
    ): boolean => {
        // An absent or empty value accepts every row.
        if (
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0)
        ) {
            return true;
        }

        if (isStringMode(mode)) {
            const query =
                typeof value === "string" ? value.trim().toLowerCase() : "";
            if (query === "") {
                return true;
            }
            return applyFilterMode(
                substringMatches(row.getValue(id), query),
                mode
            );
        }

        if (!membershipFn) {
            console.error(`Set filter mode on contains-only column "${id}"`);
            return true;
        }
        return applyFilterMode(membershipFn(row, value), mode);
    };

    return (
        row: Row<TRow>,
        id: string,
        filterValue: FilterValueWithMode<TFilterValue> | null | undefined
    ): boolean => {
        if (filterValue && !filterValue.mode) {
            console.error("Expected mode to be defined");
        }
        if (!filterValue) {
            return true;
        }

        const { value, mode, exception } = filterValue;
        return (
            matchesCondition(row, id, value, mode) &&
            matchesCondition(row, id, exception, oppositeMode(mode))
        );
    };
}

/**
 * Creates a TanStack Table filterFn with mode support.
 * Returns a filter function that handles empty values and applies the mode.
 *
 * A filter with an exception keeps only the rows that satisfy both the main
 * value under its mode and the exception under the opposite mode.
 *
 * The string modes bypass `membershipFn` and substring-match the column's
 * accessor value, so columns exposed to them through the table's mode config
 * must have an accessor returning display text (a string or string array).
 * Columns that offer only the contains modes use `createContainsOnlyFilterFn`.
 *
 * @param membershipFn - Whether the row is in the set the is/isNot filter value describes
 * @returns A filterFn compatible with TanStack Table
 *
 * @example
 * ```ts
 * filterFn: createFilterFnWithMode<TActTableRow, string[]>((row, value) =>
 *     tagsTSTFilterFn(row.original.samplingTags, value)
 * ),
 * ```
 */
export function createFilterFnWithMode<TRow, TFilterValue>(
    membershipFn: MembershipFn<TRow, TFilterValue>
) {
    return createModeFilterFn(membershipFn);
}

/**
 * For columns whose mode config offers only contains/notContains. A set mode
 * reaching this filter (e.g. from stale persisted state) is ignored.
 */
export function createContainsOnlyFilterFn<TRow>() {
    return createModeFilterFn<TRow, string>(undefined);
}
